import React, { useEffect, useState } from "react";
import { universityListingFields } from "../../formSchemas/universityFormSchemas";
import { FieldConfig, FormProps } from "./FormComponent/types";
import TagEditableList from "./FormComponent/TagEditableList";
import ObjectListEditor from "./FormComponent/ObjectListEditor";

interface UniFormProps extends FormProps {
  initialData?: Record<string, any>;
  disabled?: boolean;
}

const UniForm: React.FC<UniFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  disabled = false,
}) => {
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isEdit = !!initialData?.id;

  // ✅ Initialize fields and form data
  useEffect(() => {
    setFields(universityListingFields);

    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({});
    }
  }, [initialData]);

  // ✅ Handle value changes
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ✅ Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.values(errors).some(Boolean)) return;

    try {
      if (onSubmit) await onSubmit(formData);
    } catch (err: any) {
      console.error("Form submit error:", err);
      alert(err.message || "Failed to submit university form.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 overflow-auto">
      <div
        className={`grid gap-4 ${
          fields.length > 8 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
        }`}
      >
        {fields.map((field) => {
          const widthClass = fields.length > 8 ? "w-full" : "w-60";

          return (
            <div key={field.name} className="flex w-full">
              <label
                htmlFor={field.name}
                className="w-60 pt-2 text-gray-800 font-medium"
              >
                {field.label}:
              </label>

              {/* Tags Field */}
              {field.type === "tags" ? (
                (() => {
                  const addTagKey = `add-${field.name}`;
                  const addTagValue = formData[addTagKey] || "";

                  const handleAddTag = (newTag: string) => {
                    if (
                      newTag &&
                      !(formData[field.name] || []).includes(newTag)
                    ) {
                      setFormData({
                        ...formData,
                        [field.name]: [...(formData[field.name] || []), newTag],
                        [addTagKey]: "",
                      });
                    }
                  };

                  return (
                    <div className={`${fields.length > 8 ? "w-full" : "w-80"}`}>
                      {/* If options exist, show dropdown */}
                      {field.options && field.options.length > 0 ? (
                        <select
                          value=""
                          onChange={(e) => {
                            handleAddTag(e.target.value);
                          }}
                          disabled={disabled || field.disabled}
                          className="w-full p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100"
                        >
                          <option value="">Select {field.label}</option>
                          {field.options
                            .filter(
                              (opt) =>
                                !(formData[field.name] || []).includes(opt)
                            )
                            .map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                        </select>
                      ) : (
                        // Fallback to free typing if no options provided
                        <div className="flex gap-2 items-center">
                          <input
                            type="text"
                            name={addTagKey}
                            value={addTagValue}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                [addTagKey]: e.target.value,
                              })
                            }
                            placeholder={
                              field.placeholder || `Click to add ${field.label}`
                            }
                            disabled={disabled || field.disabled}
                            className="flex-1 p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && addTagValue.trim()) {
                                e.preventDefault();
                                handleAddTag(addTagValue.trim());
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (addTagValue.trim()) {
                                handleAddTag(addTagValue.trim());
                              }
                            }}
                            disabled={
                              disabled ||
                              field.disabled ||
                              !addTagValue.trim() ||
                              (formData[field.name] || []).includes(
                                addTagValue.trim()
                              )
                            }
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            Add
                          </button>
                        </div>
                      )}

                      {(formData[field.name] || []).length > 0 && (
                        <TagEditableList
                          tags={formData[field.name] || []}
                          fieldName={field.name}
                          setFormData={setFormData}
                          formData={formData}
                          disabled={disabled}
                          allowTagEdit={field.allowTagEdit ?? true}
                        />
                      )}
                    </div>
                  );
                })()
              ) : field.type === "objectList" ? (
                // Object List Field
                <div className={`${widthClass}`}>
                  <ObjectListEditor
                    label={field.label}
                    items={formData[field.name] || []}
                    onChange={(updatedList) =>
                      setFormData({ ...formData, [field.name]: updatedList })
                    }
                    disabled={disabled || field.disabled}
                    objectFields={field.objectFields || []}
                    widthClass={widthClass}
                  />
                </div>
              ) : field.type === "textarea" ? (
                // Textarea Field
                <textarea
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  disabled={disabled || field.disabled}
                  rows={4}
                  className={`${widthClass} p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
                />
              ) : field.type === "select" ? (
                // Select Field
                <select
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                  disabled={disabled || field.disabled}
                  required={field.required}
                  className={`${widthClass} p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100 h-9`}
                >
                  <option value="">Select</option>
                  {field.options?.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                // Default Input Field
                <input
                  type={field.type}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  disabled={disabled || field.disabled}
                  required={field.required}
                  className={`${widthClass} p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100 h-9`}
                />
              )}

              {errors[field.name] && (
                <p className="text-sm text-red-600 ml-4 mt-1">
                  {errors[field.name]}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Form Buttons */}
      <div className="flex gap-4 mt-2">
        <button
          type="submit"
          disabled={disabled || Object.values(errors).some(Boolean)}
          className="w-44 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
        >
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="w-44 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default UniForm;
